import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateHelper, DateTimeUnit } from '../../helpers/date.helper';
import { FormHelper } from '../../helpers/form.helper';

@Component({
  selector: 'shared-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: DateTimePickerComponent },
    { provide: NG_VALIDATORS, multi: true, useExisting: DateTimePickerComponent },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateTimePickerComponent implements ControlValueAccessor, OnChanges, Validator {
  @Input() dateTimeDelimiter = 'at';
  @Input() displayTime = true;
  @Input() minDate: Date = DateHelper.addTime(DateHelper.endOfYear(new Date()), -10, DateTimeUnit.Year);
  @Input() maxDate: Date = DateHelper.addTime(DateHelper.endOfYear(new Date()), 50, DateTimeUnit.Year);
  @Input() dateOutOfRangeError = 'Date out of range';
  @Input() required = true;

  form = this.formBuilder.nonNullable.group(
    {
      date: [null as Date],
      time: [{ value: null as number, disabled: !this.displayTime }],
    },
    {
      validators: [this.dateAndTimeRequiredValidator.bind(this)],
    },
  );

  disabled: boolean;
  private touched = false;
  private onChange = (dateTime: Date): void => {};
  private onTouched = (): void => {};
  private onValidatorChange: any = () => {};

  get f() {
    return this.form.controls;
  }

  get showFormErrors(): boolean {
    return this.form.invalid && (this.form.touched || this.form.dirty);
  }

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {
    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.calculateDateTime();
        this.markAsTouched();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.displayTime) {
      FormHelper.setEnabled(this.f.time, this.displayTime, false);
      this.cdr.markForCheck();
    }
  }

  private calculateDateTime(): void {
    const { date, time } = this.form.value;
    if (time == null) {
      this.onChange(date);
      return;
    }
    const dateTime = DateHelper.joinDateTime(date, time);
    this.onChange(dateTime);
  }

  writeValue(dateTime: Date): void {
    if (dateTime == null) {
      this.form.reset({ date: null, time: null }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    const { date, time } = DateHelper.splitDateTime(dateTime);
    this.form.patchValue({ date: date, time: time }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (dateTime: Date) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    FormHelper.setEnabled(this.form, !isDisabled, false);
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  validate(control: AbstractControl): ValidationErrors {
    if (this.form.invalid) {
      return { invalidDateTime: true };
    }

    if (control.value == null) {
      return this.required ? { required: true } : null;
    }

    return null;
  }

  private dateAndTimeRequiredValidator(control: AbstractControl): ValidationErrors {
    const f = control as FormGroup<DateTimeControls>;
    const { date, time } = f.value;

    if (this.displayTime) {
      if (date == null && time != null) {
        f.controls.date.setErrors({ required: true });
        return { dateRequired: true };
      } else if (date != null && time == null) {
        f.controls.time.setErrors({ required: true });
        return { timeRequired: true };
      }
    }

    if (date != null && time != null && (this.minDate != null || this.maxDate != null)) {
      const dateTime = DateHelper.joinDateTime(date, time);
      if ((this.minDate != null && dateTime < this.minDate) || (this.maxDate != null && dateTime > this.maxDate)) {
        return { dateOutOfRange: true };
      }
    }

    return null;
  }
}

interface DateTimeControls {
  date: FormControl<Date>;
  time: FormControl<number>;
}
